import { GSLEntities } from '../models/bookings';
import { Product, ProductCategories, emptyProduct } from '../models/products';

/**
 * Optional `heroImage` on any entry (self-hosted; Rezdy photos remain fallback):
 *
 *   heroImage: {
 *       webp: 'https://example.com/cdn/hero/P93AEL.webp',
 *       jpeg: 'https://example.com/cdn/hero/P93AEL.jpg',
 *   },
 *
 * WebP is offered via `<picture>`; `jpeg` is the `<img>` fallback (then Rezdy if jpeg omitted).
 *
 * Optional `displayDuration` — label for grids/cards/product page instead of deriving from Rezdy `durationMinutes`.
 * Example: displayDuration: '40 min' or 'Full day'
 */
export const baseProducts: Product[] = [
    {
        ...emptyProduct,
        entity: GSLEntities.AVIATION,
        category: ProductCategories.CAIRNS,
        productCode: 'P93AEL',
        tagline: 'See Green Island and the Great Barrier Reef',
        titleSEO: 'Scenic flight of the Great Barrier Reef and Green Island | Cairns',
        usp: [
            'Amazing views of Green Island',
            'See the Great Barrier Reef from above',
            'Guaranteed Window Seats',
            'Courtesy Bus from Cairns City',
            'Great Photo Opportunities',
        ],
        tags: [...emptyProduct.tags, 'cairns', 'popular', 'featureTile'],
        saleTips: ['Popular'],
        sales: [
            {
                startDate: '2026-04-01T00:00:00+1000',
                endDate: '2026-05-31T17:59:59+1000',
                fareTypes: [
                    {
                        label: 'Adult',
                        price: 199,
                    },
                ],
            },
        ],
        displayDuration: '40 min',
    },
    {
        ...emptyProduct,
        entity: GSLEntities.AVIATION,
        category: ProductCategories.CAIRNS,
        productCode: 'PXYRVC',
        tagline: 'See it above, then snorkel below the surface',
        titleSEO: 'Scenic flight of the Great Barrier Reef, with half day Pure Snorkelling',
        usp: [
            '40 minute scenic flight of Cairns and Green Island',
            'Half day Pure Snorkelling with Pure Snorkelling',
            'Fast access to the reef from Cairns',
            'Courtesy Bus included from Cairns City',
            'Do both in one day',
        ],
        tags: [...emptyProduct.tags, 'cairns', 'package', 'popular', 'featureTile'],
        heroImage: {
            webp: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/fly-snorkel/snorkelling-package-tour-cairns.webp',
            jpeg: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/fly-snorkel/snorkelling-package-tour-cairns.JPG',
        },
        displayDuration: 'Full Day',
    },
    {
        ...emptyProduct,
        entity: GSLEntities.AVIATION,
        category: ProductCategories.CAIRNS,
        productCode: 'P01FES',
        tagline: 'See the Great Barrier Reef from above & below',
        titleSEO: 'Scenic flight of the Great Barrier Reef, with Green Island cruise from Cairns',
        usp: [
            '40 minute scenic flight of Cairns and Green Island',
            'Cruise to Green Island with Big Cat',
            'Includes lunch, snorkelling, and Green Island',
            'Courtesy Bus included from Cairns City',
            'Full Day Adventure',
        ],
        tags: [...emptyProduct.tags, 'cairns', 'package', 'popular', 'featureTile'],
        heroImage: {
            webp: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/reef-hopper/green-island-scenic-flight-cairns.webp',
            jpeg: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/reef-hopper/green-island-scenic-flight-cairns.jpg',
        },
        displayDuration: 'Full Day',
        sales: [
            {
                startDate: '2023-02-20T00:00:00+1000',
                endDate: '2023-03-31T17:59:59+1000',
                fareTypes: [
                    {
                        label: 'Adult',
                        price: 289,
                    },
                    {
                        label: 'Child (4 to 14 years old)',
                        price: 239,
                    },
                    {
                        label: 'Child (3 years old)',
                        price: 199,
                    },
                ],
            },
        ],
    },
    {
        ...emptyProduct,
        entity: GSLEntities.AVIATION,
        category: ProductCategories.CAIRNS,
        productCode: 'PSQ9LK',
        tagline: 'Fly over the Reef, and sail with Passions of Paradise',
        titleSEO: 'Scenic flight of the Great Barrier Reef, with Passions of Paradise Cruise from Cairns',
        usp: [
            '40 minute scenic flight of Cairns and Green Island',
            'Cruise to the Great Barrier Reef with Passions of Paradise',
            'Full day sail to the Great Barrier Reef',
            'Includes snorkelling at two locations',
            'Optional diving available on-board',
        ],
        tags: [...emptyProduct.tags, 'cairns', 'package', 'popular', 'featureTile'],
        heroImage: {
            webp: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/fly-sail/fly-sail-package-tour-cairns.webp',
            jpeg: 'https://gslgroup-media.s3.ap-southeast-2.amazonaws.com/gslaviation/products/fly-sail/fly-sail-package-tour-cairns.jpg',
        },
        displayDuration: 'Over 2 Days',
        sales: [
            {
                startDate: '2023-02-20T00:00:00+1000',
                endDate: '2023-03-31T17:59:59+1000',
                fareTypes: [
                    {
                        label: 'Adult',
                        price: 369,
                    },
                    {
                        label: 'Child (3 to 14 years old)',
                        price: 309,
                    },
                    {
                        label: '2 Adults + 2 Children',
                        price: 1299,
                    },
                ],
            },
        ],
    },
];

export const sitemapArray = baseProducts.map((p) => ({
    productCode: p.productCode,
    entity: p.entity,
    category: p.category || null,
}));

/*

 {
        ...emptyProduct,
        entity: GSLEntities.AVIATION,
        category: ProductCategories.CAIRNS,
        productCode: 'P3SACG',
        tagline: 'See the Great Barrier Reef and Kuranda Rainforest',
        titleSEO: 'Scenic flight of the Great Barrier Reef, with Skyrail and Kuranda railway',
        usp: [
            '40 minute scenic flight of Cairns and Green Island',
            'Skyrail to Kuranda',
            'Scenic Railway return to Cairns',
            'Courtesy Bus included from Cairns City',
            'Full Day Adventure'
        ],
        tags: [...emptyProduct.tags, 'cairns', 'package', 'popular', 'featureTile'],
        sales: [
            {
                startDate: '2023-02-20T00:00:00+1000',
                endDate: '2023-03-31T17:59:59+1000',
                fareTypes: [
                    {
                        label: 'Adult',
                        price: 279
                    },
                    {
                        label: 'Child (4 to 14 years old)',
                        price: 239
                    },
                    {
                        label: 'Child (3 years old)',
                        price: 199
                    }
                ]
            }
        ]
    },

    */
